<script setup lang="ts">
import type { DRPartialProject, Project } from "@/types";
import ProjectAvatar from "./ProjectAvatar.vue";
import { RouterLink } from "vue-router";
import ProjectActionsDropdown from "./ProjectActionsDropdown.vue";
import ProjectStatusSelector from "./ProjectStatusSelector.vue";

import { useDates } from "@/composable/useDates";
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { ItemDisplayMode } from "@/stores/Settings";
import DRDisplayItem from "../UI/items/DRDisplayItem.vue";

const { formatDistanceToNow } = useDates();
const { t } = useTranslation();
const props = withDefaults(
  defineProps<{
    project: DRPartialProject | Project;
    display?: ItemDisplayMode;
    readonly?: boolean;
  }>(),
  {
    display: "card",
    readonly: false,
  }
);

const insights = computed(() => [
  {
    icon: "bi-files",
    label: props.project.drawingsCount,
  },
  {
    icon: "bi-geo-alt",
    label: props.project.diagnosticsCount,
  },
]);

const subtitle = computed(() =>
  props.project.createdAt
    ? t("created") + ` ${formatDistanceToNow(props.project.createdAt)}`
    : ""
);
</script>

<template>
  <RouterLink
    :to="{
      name: 'project',
      params: { id: props.project.id },
    }"
    custom
    v-slot="{ href, navigate }"
  >
    <DRDisplayItem
      :display="display"
      :href="href"
      @click="navigate"
      :title="project.name"
      :subtitle="subtitle"
    >
      <template #icon="{ size }">
        <ProjectAvatar :project="project" :size="size + 5"></ProjectAvatar>
      </template>

      <template #actions>
        <ProjectActionsDropdown :project="project" :readonly="readonly" />
      </template>
      <template #append1>
        <div class="d-flex flex-row gap-1 align-items-center me-3">
          <div
            v-for="item in insights"
            :key="item.icon"
            style="width: 3rem"
            class="col-auto d-flex gap-1 align-items-center text-grey"
          >
            <el-icon size="1.2rem"><i :class="item.icon"></i></el-icon
            ><span style="font-size: 12px">
              {{ item.label }}
            </span>
          </div>
        </div>
      </template>
      <template #append2>
        <div class="col-auto">
          <ProjectStatusSelector :project="project" :disabled="readonly" /></div
      ></template>
    </DRDisplayItem>
  </RouterLink>
</template>

<style lang="scss" scoped></style>
